import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import type { EChartsType } from 'echarts';
import { formatKMB } from '@intelligence/v3/utils/number';
import type { ITodayLastPointMeta } from './type';

/**
 * 末端 CCU 标签水平偏移（靠右时向左偏避免裁切）
 */
const getTodayLastPointLabelOffsetX = (
  formattedValue: string,
  dataIndex: number,
  totalSlots: number,
): number => {
  const { length } = formattedValue;
  const getXOffset = (len: number): number => {
    if (len <= 3) return 8;
    if (len <= 4) return 12;
    if (len <= 6) return 14;
    if (len <= 8) return 16;
    if (len <= 10) return 18;
    if (len <= 12) return 20;
    return 22;
  };
  if (dataIndex === 0) {
    return getXOffset(length);
  }
  if (dataIndex === totalSlots - 1) {
    return -getXOffset(length) + 6;
  }
  return 0;
};

interface IChartPointRippleProps {
  chartInstance: React.RefObject<EChartsType | null>;
  point: ITodayLastPointMeta | null;
}

interface IRipplePosition {
  left: number;
  top: number;
  visible: boolean;
}

const HIDDEN_POSITION: IRipplePosition = {
  left: 0,
  top: 0,
  visible: false,
};

/**
 * 图表数据点涟漪动效组件
 * 通过 DOM 叠加 + CSS 动画实现涟漪效果
 * 自动跟随 ECharts 图表的数据点位置
 */
const ChartPointRipple: React.FC<IChartPointRippleProps> = ({
  chartInstance,
  point,
}) => {
  const [position, setPosition] = useState<IRipplePosition>(HIDDEN_POSITION);
  const updatePositionRef = useRef<((params?: any) => void) | null>(null);

  // 计算格式化值（缓存以避免重复计算）
  const formattedValue = useMemo(() => {
    if (!point) return '';
    return formatKMB(point.value);
  }, [point?.value]);

  // 计算标签偏移量（依赖格式化值和相关点信息）
  const labelOffsetX = useMemo(() => {
    if (!point) return 0;
    return getTodayLastPointLabelOffsetX(
      formattedValue,
      point.dataIndex,
      point.totalSlots,
    );
  }, [formattedValue, point?.dataIndex, point?.totalSlots]);

  // 位置计算函数，使用 useCallback 保持稳定引用
  const updatePosition = useCallback((params?: any): void => {
    const instance = chartInstance.current;
    if (!instance || instance.isDisposed() || !point) {
      setPosition(HIDDEN_POSITION);
      return;
    }

    const { dataIndex, value, seriesName } = point;

    // 主动检查系列在图例中的可见状态
    const option = instance.getOption() as Record<string, any>;
    const selected = (option?.legend?.[0] as any)?.selected ?? {};
    
    if (selected[seriesName] === false) {
      setPosition(HIDDEN_POSITION);
      return;
    }

    // 处理图例切换：如果当前系列被隐藏，则隐藏涟漪效果
    if (
      params?.type === 'legendselectchanged' &&
      params?.name === seriesName &&
      params?.selected?.[seriesName] === false
    ) {
      setPosition(HIDDEN_POSITION);
      return;
    }

    try {
      const pixel = instance.convertToPixel(
        { xAxisIndex: 0, yAxisIndex: 0 },
        [dataIndex, value],
      );

      if (!Array.isArray(pixel) || pixel.length < 2) {
        setPosition(HIDDEN_POSITION);
        return;
      }

      const [left = 0, top = 0] = pixel;
      
      // 简化可见性判断：只要 convertToPixel 返回有效坐标就显示
      const isValidPosition = left > 0 && top > 0 && isFinite(left) && isFinite(top);

      setPosition({
        left,
        top,
        visible: isValidPosition,
      });
    } catch (error) {
      // ECharts 实例可能在转换过程中变为无效（如组件卸载、图表销毁）
      // 静默处理，这是预期行为，无需记录
      if (instance && !instance.isDisposed()) {
        setPosition(HIDDEN_POSITION);
      }
    }
  }, [chartInstance, point]);

  // 使用 requestAnimationFrame 定期检查 chartInstance.current 是否可用
  // 并在可用时添加事件监听器和计算位置
  useEffect(() => {
    if (!point) {
      setPosition(HIDDEN_POSITION);
      return;
    }

    let rafId: number | null = null;
    let isMounted = true;

    const checkAndSetup = () => {
      if (!isMounted) return;

      const instance = chartInstance.current;

      if (instance && !instance.isDisposed()) {
        // 实例可用，添加事件监听器
        updatePositionRef.current = updatePosition;
        instance.on('finished', updatePosition);
        instance.on('legendselectchanged', updatePosition);

        // 初始计算位置
        updatePosition();

        return; // 停止检查
      }

      // 实例不可用，继续检查
      rafId = requestAnimationFrame(checkAndSetup);
    };

    // 开始检查
    rafId = requestAnimationFrame(checkAndSetup);

    return () => {
      isMounted = false;
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }

      const instance = chartInstance.current;
      if (instance && !instance.isDisposed() && updatePositionRef.current) {
        instance.off('finished', updatePositionRef.current);
        instance.off('legendselectchanged', updatePositionRef.current);
      }
    };
  }, [chartInstance, point, updatePosition]);

  if (!point || !position.visible) {
    return null;
  }

  return (
    <div
      className="steam-hourly-ccu__ripple-wrap"
      style={{
        left: `${position.left}px`,
        top: `${position.top}px`,
      }}
      aria-label={`Data point ripple: ${formattedValue}`}
    >
      <span
        className="steam-hourly-ccu__ripple-label"
        style={{
          transform: `translate(calc(-50% + ${labelOffsetX}px), 0)`,
        }}
      >
        {formattedValue}
      </span>
      <span className="steam-hourly-ccu__ripple-wave" style={{ animationDelay: '0s' }} />
      <span className="steam-hourly-ccu__ripple-wave" style={{ animationDelay: '1s' }} />
      <span className="steam-hourly-ccu__ripple-wave" style={{ animationDelay: '2s' }} />
      <span className="steam-hourly-ccu__ripple-dot" />
    </div>
  );
};

export default React.memo(ChartPointRipple);
